

import {
  createUserWithEmailAndPassword,
  FacebookAuthProvider,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  RecaptchaVerifier,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPhoneNumber,
  signInWithPopup,
  signOut,
  TwitterAuthProvider
} from 'firebase/auth';
import { useCallback, useContext, useState } from 'react';

import { serverTimestamp, writeBatch } from 'firebase/firestore';
import { firebaseSignIns } from 'global/auth/signInActions/signInMethods';
import { signInResponseTypes } from 'global/cnr/reducers/SignInReducer';
import { _collectionName_appUserValidation, _emailSignInStorageItem } from 'global/cnr/reducers/ValidationReducer';
import { createInitials } from 'global/common/dataAdjust';
import { gEnums } from 'global/enums/globalEnums';
import { createRefPath, createRefPath_client } from 'global/firestoreData/appData/appRefPaths';
import _ from 'lodash';
import { AppContext } from '../AppProvider';
import { DatabaseContext } from '../DatabaseContext';
import { ParentContext } from '../ParentContext';
import { CurrentSettingsContext } from '../SettingsDataContext';
import { useFunctions } from './useFunctions';
import { useHost } from './useHost';

export function useSignIn() {

  const { fn_cls } = useFunctions()

  const currentSettingsContext = useContext(CurrentSettingsContext)
  const { currentSettings_state } = currentSettingsContext ?? {}
  const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
  const { appSignIn } = aps_global ?? {}

  const appContext = useContext(AppContext)
  const { app_state } = appContext ?? {}
  const { mainAuth, mainDb } = app_state

  const databaseContext = useContext(DatabaseContext)
  const { database_fns } = databaseContext ?? {}

  const parentContext = useContext(ParentContext);
  const { states } = parentContext ?? {}
  const { paps_state } = states ?? {}
  const { pathViews } = paps_state ?? {}

  let _currentAuth = mainAuth
  let _currentDb = mainDb

  let appName = 'ME'

  const auth_current = useHost()

  const [user, setUser] = useState(auth_current.currentUser);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [notification, setNotification] = useState();

  const googleLink = async () => {
    signIn_googleLink(mainAuth)
  }

  const setupRecaptcha = useCallback(async () => {
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
    }

    const verifier = new RecaptchaVerifier(
      mainAuth,
      'recaptcha-container',
      {
        size: 'invisible',
        callback: () => { },
        'expired-callback': () => {
          console.log('reCAPTCHA expired');
        }
      }
    );

    window.recaptchaVerifier = verifier;

    try {
      const widgetId = await verifier.render();

      console.log('reCAPTCHA ready:', widgetId);
      return verifier;
    } catch (error) {
      console.error('reCAPTCHA render error:', error);
      throw error;
    }
  }, [mainAuth]);


  const createAccount = async (creds) => {
    const { email, password } = creds ?? {}
    if (!email || !password) {
      setNotification({
        title: 'No Email/Password',
        content: `You have not provided an email/password.`,
      })
      return;
    }
    setLoading(true);
    try {
      const user = await createUserWithEmailAndPassword(
        auth_current,
        email,
        password
      )
      setUser(user);
      setError(null);
      setNotification({
        title: 'Success',
        content: `You are now signed in with the email: ${email}.`,
      })
      const cb = () => {

      }
      createAccount_db(database_fns, user, pathViews, cb)
    } catch (err) {
      setError(err);
      setNotification({
        title: 'Create Account unsuccessfull',
        content: err.message,
      })
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Email & Password
  // -----------------------------
  const emailPasswordSignIn = async (creds, profileData) => {

    const { email, password } = creds ?? {}

    if (!email || !password) {
      setNotification({
        title: 'No Email/Password',
        content: `You have not provided an email/password.`,
      })
      return;
    }

    setLoading(true);

    const siProps = { hasAuth: false, appSignIn, profileData }

    try {
      const cred = await signInWithEmailAndPassword(mainAuth, email, password);
      setUser(cred.user);
      setError(null);
      setNotification({
        title: 'Success',
        content: `You are now logged in with the email: ${email}.`,
      })
      setSuccess(true)
      return cred.user;
    } catch (err) {
      const response = await emailPasswordFail(fn_cls, database_fns, _currentAuth, creds, siProps, err)
      setError(err);
      console.log('response', response)
      setNotification({
        title: response?.title,
        content: response?.content,
      })
      setSuccess(false)
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Email Link (Passwordless)
  // -----------------------------
  const sendEmailLink = async (creds) => {
    const { email, options } = creds ?? {}
    if (!email) {
      setNotification({
        title: 'No Email',
        content: `You have not provided an email.`,
      })
      return;
    }
    setLoading(true);
    try {
      const params = {
        isEmailLink: true,
        emailSignInConfirmation: true,
        openInSamePWA: true,
        email,
      }
      const currentUrl = window.location.href
      var actionCodeSettings = {
        url: currentUrl + '?fromTs',
        handleCodeInApp: true,
      };
      if (actionCodeSettings.url) {
        Object.keys(params).forEach(paramName => {
          actionCodeSettings.url += `${actionCodeSettings.url.includes('?') ? '&' : '?'}${paramName}=${params[paramName]}`;
        });
      }
      console.log('options 1', auth_current.app.options)
      await sendSignInLinkToEmail(auth_current, email, actionCodeSettings);

      window.localStorage.setItem(_emailSignInStorageItem, email);
      window.localStorage.setItem(`${_emailSignInStorageItem}_options`, JSON.stringify(options));

      setNotification({
        title: 'Email has been sent',
        content: `An email has been sent to ${email}.`,
      })
      setError(null);
    } catch (err) {
      setError(err);
      setNotification({
        title: `Email has NOT been sent to ${email}`,
        content: err.message,
      })
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const completeEmailLinkSignIn = async (email) => {
    setLoading(true);
    try {
      console.log('options 2', auth_current.app.options)
      if (isSignInWithEmailLink(auth_current, window.location.href)) {
        if (email) {
          const result = await signInWithEmailLink(auth_current, email, window.location.href);
          const user = result?.user;
          setUser(user);
          const options = window.localStorage.getItem(`${_emailSignInStorageItem}_options`);
          window.localStorage.removeItem(_emailSignInStorageItem);
          window.localStorage.removeItem(`${_emailSignInStorageItem}_options`);
          setError(null);
          setSuccess(true);
          setNotification({
            title: `Email Confirmation`,
            content: 'You are now signed in to the app. You can safely close this page.',
          })
          appUserValidation_update(_currentDb, database_fns, email, gEnums.emailValidationTypes.activated, null, options)
        } else {
          setSuccess(false);
          setNotification({
            title: `No email found`,
            content: 'No email found during validation.',
          })
        }
        return user;
      }
    } catch (err) {
      setError(err);
      setSuccess(false);
      setNotification({
        title: `There was an issue with the confirmation link to ${email}`,
        content: err.message,
      })
    } finally {
      setLoading(false);
    }
  };

  const isSignInEmailLink = async (url) => {
    return isSignInWithEmailLink(auth_current, url)
  }

  // -----------------------------
  // Popup Providers (Google, Facebook, Twitter)
  // -----------------------------
  const popupSignIn = async (providerName) => {
    setLoading(true);
    try {
      let provider;
      switch (providerName) {
        case 'google': provider = new GoogleAuthProvider(); break;
        case 'facebook': provider = new FacebookAuthProvider(); break;
        case 'twitter': provider = new TwitterAuthProvider(); break;
        default: throw new Error('Unknown provider');
      }
      const cred = await signInWithPopup(auth_current, provider);
      setUser(cred.user);
      setError(null);
      setNotification({
        title: 'Success',
        content: `You are now signed in in with ${providerName}`,
      })
      return cred.user;
    } catch (err) {
      setError(err);
      setNotification({
        title: `There was an signing in with to your ${providerName} account`,
        content: err.message,
      })
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Phone Number Sign-In
  // -----------------------------

  const phoneSignIn = async (phoneNumber) => {
    setLoading(true);
    setError(null);

    try {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }

      const verifier = new RecaptchaVerifier(mainAuth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {
          console.log('reCAPTCHA solved');
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired');
        }
      });

      window.recaptchaVerifier = verifier;

      const confirmationResult = await signInWithPhoneNumber(
        mainAuth,
        phoneNumber,
        verifier
      );

      setNotification({
        title: 'Success',
        content: `You are now signed in with your phone number ${phoneNumber}`,
      });

      return confirmationResult;

    } catch (err) {
      console.error('Phone sign-in error:', err);

      setError(err);

      setNotification({
        title: `There was an issue signing in with your phone number: ${phoneNumber}`,
        content: err.message,
      });

      throw err;

    } finally {
      setLoading(false);
    }
  };

  const phoneSignInXXX = async (phoneNumber) => {
    setLoading(true);
    try {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
      }

      const verifier = new RecaptchaVerifier(mainAuth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => { },
        'expired-callback': () => console.log('reCAPTCHA expired')
      });

      window.recaptchaVerifier = verifier;

      await verifier.render();

      const confirmationResult = await signInWithPhoneNumber(mainAuth, phoneNumber, verifier);
      setError(null);
      setNotification({
        title: 'Success',
        content: `You are now signed in in with you phone number ${phoneNumber}`,
      })
      return confirmationResult; // confirmationResult.confirm(code) later
    } catch (err) {
      setError(err);
      setNotification({
        title: `There was an issue signing in with your phone number: ${phoneNumber}`,
        content: err.message,
      })
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Sign out
  // -----------------------------
  const signOutUser = async () => {
    try {
      await signOut(auth_current);
      setUser(null);
    } catch (err) {
      setError(err);
      console.error(err);
    }
  };

  return {
    appName,
    auth: auth_current,
    completeEmailLinkSignIn,
    createAccount,
    emailPasswordSignIn,
    error,
    googleLink,
    isSignInEmailLink,
    notification,
    phoneSignIn,
    popupSignIn,
    sendEmailLink,
    setupRecaptcha,
    signOutUser,
    success,
    updating: loading,
    user,
  };
}

const appUserValidation_update = async (db, database_fns, email, status, response, options) => {

  const _refPath = createRefPath([_collectionName_appUserValidation, email])
  const _ts = serverTimestamp()

  let dataToUpdate;
  let dataToSet;

  switch (status) {
    case gEnums.emailValidationTypes.requested:
      if (response) {
        const { result } = response ?? {}
        const { link } = result ?? {}
        var urlParams = new URLSearchParams(link);
        var oobCode = _.get(Object.fromEntries(urlParams), 'oobCode');
        dataToSet = { email, status, oobCode, link, pendingTimestamp: _ts }
      }
      break;
    case gEnums.emailValidationTypes.requestedNonLink:
      dataToSet = { email, status, pendingTimestamp: _ts }
      break;
    case gEnums.emailValidationTypes.accepted:
      dataToUpdate = { status, acceptedTimestamp: _ts }
      break;
    case gEnums.emailValidationTypes.denied:
      dataToUpdate = { status, deniedTimestamp: _ts }
      break;
    default:
      dataToUpdate = { status }
      if (options) {
        dataToUpdate.options = JSON.parse(options);
      }
    // nothing
  }

  const batch = writeBatch(db);

  if (dataToSet) {
    database_fns.set_doc(_refPath, dataToSet, true, false, batch)
  } else if (dataToUpdate) {
    console.log('_refPath', _refPath, dataToUpdate)
    database_fns.update_doc(_refPath, dataToUpdate, null, batch)
  }

  batch.commit()
}

const createAccount_db = (database_fns, currentUser, pathViews, callback) => {
  const { email: email_current } = currentUser ?? {}
  if (email_current === email) {
    const dataToUpdate = {
      email: email,
      firstName,
      lastName,
      displayName: firstName + ' ' + lastName,
      initials: createInitials(firstName + ' ' + lastName)
    }
    switch (createProfileType) {
      case gEnums.createProfileTypes.clientProfiles:
        const _refPath_c = createRefPath_client(pathViews, ['profiles', email])
        database_fns.set_doc(_refPath_c, dataToUpdate, null, callback)
        break;
      case gEnums.createProfileTypes.profiles:
        const _refPath_p = createRefPath('users')
        database_fns.add_doc(_refPath_p, dataToUpdate, callback)
        break;
      default:
      // nothing
    }
  }
}

const emailPasswordFail = async (fn_cls, database_fns, mainAuth, authCreds, siProps, err) => {

  const { appSignIn, allowSignUp, signInData, directSignIn } = siProps
  const { authGlobalPassword } = appSignIn ?? {}

  //  sign in with the email/password FAILED
  const { code, message: errorMessage } = err ?? {};

  switch (code) {

    case 'auth/too-many-requests':
      return {
        title: _.startCase(signInResponseTypes.tooManyAttempts),
        content: _.startCase(signInResponseTypes.tooManyAttempts)
      };

    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      if ((authGlobalPassword === authCreds.password)) {
        const response = await signInWithGlobalPw(fn_cls, mainAuth, authCreds, siProps)
        return response
      } else {
        return {
          title: _.startCase(signInResponseTypes.incorrectPassword),
          content: _.startCase(signInResponseTypes.incorrectPassword)
        };
      }
    // return {
    //   title: _.startCase(signInResponseTypes.incorrectPassword),
    //   content: _.startCase(signInResponseTypes.incorrectPassword)
    // };

    default:
      // if we allow authGlobalPassword, continue
      if (!directSignIn) {
        if ((authGlobalPassword === authCreds.password)) {
          const response = await signInWithGlobalPw(fn_cls, mainAuth, authCreds, siProps)
          return response
        } else {
          return {
            title: _.startCase(signInResponseTypes.incorrectPassword),
            content: _.startCase(signInResponseTypes.incorrectPassword)
          };
        }
      } else {
        if (allowSignUp) {
          await createNewAuth(database_fns, mainAuth, authCreds)
        } else {
          return {
            title: signInResponseTypes.incorrectPassword,
            content: 'Log-in Error'
          }
        }
      }
  }
}

const signInWithGlobalPw = async (fn_cls, mainAuth, authCreds, siProps) => {

  const fsi = firebaseSignIns(mainAuth)

  const { hasAuth, appSignIn, profileData } = siProps
  const { firstName, lastName, displayName } = profileData ?? {}

  const data_global = { email: authCreds.email, password: authCreds.password, firstName, lastName, displayName };

  if (data_global.email && data_global.password) {
    if (hasAuth) {
      // if the user already has authentication, and has not already been alerted, alert the user that their password will be changed
      return { title: signInResponseTypes.handlePasswordAlert, content: 'Already Authorized' };
    } else {
      const response = await fn_cls.createOrUpdateAuthAndSignIn(data_global)
      const { success } = response ?? {}
      if (success === true) {
        let resp = await fsi.emailAndPassword(authCreds)
        if (resp?.success) {
          return {
            title: 'Success',
            content: ''
          };
        } else {
          return {
            title: 'Success',
            content: resp?.signInResponseType
          };
        }
        // if (sendEmailVerification) { sendEmailVerificationToCurrentUser(mainAuth); }
      } else {
        return {
          title: 'Sign In Error',
          content: _.startCase(signInResponseTypes.signInError)
        };
      }
    }
  }
}

const createNewAuth = async (database_fns, mainAuth, authCreds) => {

  try {
    await createUserWithEmailAndPassword(
      mainAuth,
      authCreds.email.toLowerCase(),
      authCreds.password
    );

    var user = mainAuth.currentUser;

    database_fns.get_data({ refPath: ['profiles'], wheres: [where('email', '==', user.email)] }).then(profile => {
      database_fns.add_doc(['profiles'], profile);
      database_fns.delete_doc(['profiles', user.email]);
    });

  } catch (err) {
    return ({ content: _.startCase(signInResponseTypes.signUpError), err, title: _.startCase(signInResponseTypes.signUpError) });
  }
}

const signIn_googleLink = async (mainAuth) => {

  const provider = new GoogleAuthProvider()

  try {
    const result = await signInWithPopup(mainAuth, provider)
    const credential = GoogleAuthProvider.credentialFromResult(result)
    console.log('credential', credential)
    return result

  } catch (error) {
    console.error('Google sign in error', error)
    throw error
  }
}

// const provider = new GoogleAuthProvider();

// signInWithPopup(mainAuth, provider)
//   .then((result) => {
//     // This gives you a Google Access Token. You can use it to access the Google API.
//     const credential = GoogleAuthProvider.credentialFromResult(result);
//     console.log('credential', credential)
//     // const token = credential.accessToken;
//     // The signed-in user info.
//     // const user = result.user;
//     // IdP data available using getAdditionalUserInfo(result)
//     // ...
//   }).catch((error) => {
//     // Handle Errors here.
//     // const errorCode = error.code;
//     // const errorMessage = error.message;
//     // The email of the user's account used.
//     // const email = error.customData.email;
//     // The AuthCredential type that was used.
//     // const credential = GoogleAuthProvider.credentialFromError(error);
//     // ...
//   })
// }
